import { useCallback, useMemo } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { ApiError, type ActionOpts, type BulkOp, type BulkResult, type ListQuery, type Paged } from './client';
import { useApi } from './context';
import { useDataSource } from '../meta/useDataSource';
import { useToast } from '../design/primitives/Toast';

// React-query hooks over the client. Query keys are namespaced per resource so a mutation
// can invalidate everything about a resource with one call. Mutations toast, invalidate,
// and (for updates) offer Undo — the previous values are re-applied through the same
// PATCH, which is safe exactly when the fields are updatable.

export const resourceKeys = {
  all: (name: string) => ['res', name] as const,
  lists: (name: string) => ['res', name, 'list'] as const,
  list: (name: string, query: ListQuery | undefined) => ['res', name, 'list', query ?? {}] as const,
  items: (name: string) => ['res', name, 'item'] as const,
  item: (name: string, id: string, opts?: unknown) => ['res', name, 'item', id, opts ?? {}] as const,
  meta: (name: string) => ['meta', name] as const,
};

export interface UseResourceListOpts {
  enabled?: boolean;
  /** Page size (default 50). */
  limit?: number;
  staleTime?: number;
  /** Keep previous data while the query changes (no flash of empty). */
  keepPrevious?: boolean;
}

export function useResourceList<T = Record<string, unknown>>(name: string, query?: ListQuery, opts: UseResourceListOpts = {}) {
  const api = useApi();
  // A resource the tenant's connected system does not serve is never asked for (it is a certain 501).
  const served = useDataSource().resourceServed(name);
  const limit = opts.limit ?? 50;
  const q = useInfiniteQuery({
    queryKey: resourceKeys.list(name, { ...query, limit }),
    queryFn: ({ pageParam }) => api.resource<T>(name).list({ ...query, limit, cursor: pageParam as string | null }),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Paged<T>) => last.next_cursor ?? null,
    enabled: (opts.enabled ?? true) && served,
    staleTime: opts.staleTime,
    placeholderData: opts.keepPrevious ? (prev) => prev : undefined,
  });
  const rows = useMemo(() => q.data?.pages.flatMap((p) => p.data) ?? [], [q.data]);
  return {
    rows,
    pages: q.data?.pages ?? [],
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    isError: q.isError,
    error: q.error as ApiError | null,
    hasMore: q.hasNextPage ?? false,
    loadingMore: q.isFetchingNextPage,
    loadMore: () => void q.fetchNextPage(),
    refetch: () => void q.refetch(),
    isPlaceholder: q.isPlaceholderData,
  };
}

export function useResource<T = Record<string, unknown>>(name: string, id: string | null | undefined, opts: { expand?: string[]; enabled?: boolean; staleTime?: number } = {}) {
  const api = useApi();
  const served = useDataSource().resourceServed(name);
  return useQuery({
    queryKey: resourceKeys.item(name, id ?? '', { expand: opts.expand }),
    queryFn: () => api.resource<T>(name).get(id!, { expand: opts.expand }),
    enabled: Boolean(id) && (opts.enabled ?? true) && served,
    staleTime: opts.staleTime,
  });
}

export function useInvalidateResource() {
  const qc = useQueryClient();
  return useCallback(
    (name: string, id?: string) => {
      void qc.invalidateQueries({ queryKey: resourceKeys.lists(name) });
      if (id) void qc.invalidateQueries({ queryKey: resourceKeys.item(name, id) });
      else void qc.invalidateQueries({ queryKey: resourceKeys.items(name) });
      // Cross-cutting caches that mirror resource state.
      if (name === 'custom-field-defs') void qc.invalidateQueries({ queryKey: ['meta'] }); // definitions become fields in every form
      void qc.invalidateQueries({ queryKey: ['activity'] });
      void qc.invalidateQueries({ queryKey: ['stats'] });
    },
    [qc],
  );
}

export interface MutationToast {
  /** false = silent. A string or function = success title. */
  success?: false | string | ((result: unknown) => string);
  error?: false | string;
}

interface CommonOpts<TData, TVars> {
  toast?: MutationToast;
  onSuccess?: (data: TData, vars: TVars) => void;
  onError?: (error: ApiError, vars: TVars) => void;
  /** Extra resources to invalidate on success. */
  invalidate?: string[];
}

function useCommon<TData, TVars>(name: string, opts: CommonOpts<TData, TVars>, defaults: { success: string; error: string }) {
  const toast = useToast();
  const invalidate = useInvalidateResource();
  const onSuccess = (data: TData, vars: TVars) => {
    invalidate(name);
    for (const r of opts.invalidate ?? []) invalidate(r);
    const s = opts.toast?.success;
    if (s !== false) toast({ title: typeof s === 'function' ? s(data) : (s ?? defaults.success) });
    opts.onSuccess?.(data, vars);
  };
  const onError = (e: unknown, vars: TVars) => {
    const err = e instanceof ApiError ? e : new ApiError(0, e instanceof Error ? e.message : String(e));
    if (opts.toast?.error !== false && !err.isValidation) toast({ kind: 'error', title: opts.toast?.error ?? defaults.error, detail: err.detail });
    opts.onError?.(err, vars);
  };
  return { toast, invalidate, onSuccess, onError };
}

export function useCreate<Row = Record<string, unknown>, Create = Partial<Row>>(name: string, opts: CommonOpts<Row, Create> = {}) {
  const api = useApi();
  const c = useCommon<Row, Create>(name, opts, { success: 'Created', error: 'Could not create' });
  return useMutation<Row, ApiError, Create>({
    mutationFn: (body) => api.resource<Row, Create>(name).create(body),
    onSuccess: c.onSuccess,
    onError: c.onError,
  });
}

export interface UpdateVars<Update> {
  id: string;
  body: Update;
  /** Previous values of the changed fields, enabling Undo. */
  previous?: Update;
}

export function useUpdate<Row = Record<string, unknown>, Update = Partial<Row>>(name: string, opts: CommonOpts<Row, UpdateVars<Update>> & { optimistic?: boolean; undo?: boolean } = {}) {
  const api = useApi();
  const qc = useQueryClient();
  const c = useCommon<Row, UpdateVars<Update>>(name, { ...opts, toast: { ...opts.toast, success: false } }, { success: 'Saved', error: 'Could not save' });
  const mutation = useMutation<Row, ApiError, UpdateVars<Update>, { snapshots: [readonly unknown[], unknown][] }>({
    mutationFn: ({ id, body }) => api.resource<Row, Partial<Row>, Update>(name).update(id, body),
    onMutate: async (vars) => {
      if (opts.optimistic === false) return { snapshots: [] };
      await qc.cancelQueries({ queryKey: resourceKeys.all(name) });
      const snapshots = qc.getQueriesData({ queryKey: resourceKeys.all(name) });
      // Patch the item cache + every loaded list page in place.
      qc.setQueriesData({ queryKey: resourceKeys.items(name) }, (old: unknown) => {
        if (old && typeof old === 'object' && (old as { id?: string }).id === vars.id) return { ...(old as object), ...(vars.body as object) };
        return old;
      });
      qc.setQueriesData({ queryKey: resourceKeys.lists(name) }, (old: unknown) => {
        const inf = old as { pages?: Paged<Record<string, unknown>>[] } | undefined;
        if (!inf?.pages) return old;
        return {
          ...inf,
          pages: inf.pages.map((p) => ({ ...p, data: p.data.map((r) => (r.id === vars.id ? { ...r, ...(vars.body as object) } : r)) })),
        };
      });
      return { snapshots: snapshots as [readonly unknown[], unknown][] };
    },
    onError: (e, vars, ctx) => {
      for (const [key, data] of ctx?.snapshots ?? []) qc.setQueryData(key, data);
      c.onError(e, vars);
    },
    onSuccess: (data, vars) => {
      c.onSuccess(data, vars);
      if (opts.toast?.success === false) return;
      const title = typeof opts.toast?.success === 'function' ? opts.toast.success(data) : (opts.toast?.success ?? 'Saved');
      const undoable = opts.undo !== false && vars.previous && Object.keys(vars.previous as object).length > 0;
      c.toast({
        title,
        action: undoable
          ? {
              label: 'Undo',
              run: async () => {
                await api.resource<Row, Partial<Row>, Update>(name).update(vars.id, vars.previous!);
                c.invalidate(name, vars.id);
              },
            }
          : undefined,
      });
    },
  });
  return mutation;
}

export function useRemove(name: string, opts: CommonOpts<void, string> = {}) {
  const api = useApi();
  const c = useCommon<void, string>(name, opts, { success: 'Deleted', error: 'Could not delete' });
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => api.resource(name).remove(id),
    onSuccess: c.onSuccess,
    onError: c.onError,
  });
}

export function useAction<R = unknown, Body = unknown>(name: string, action: string, opts: CommonOpts<R, ActionOpts & { body?: Body }> & { method?: ActionOpts['method'] } = {}) {
  const api = useApi();
  const c = useCommon<R, ActionOpts & { body?: Body }>(name, opts, { success: 'Done', error: 'Action failed' });
  return useMutation<R, ApiError, ActionOpts & { body?: Body }>({
    mutationFn: (o) => api.resource(name).action<R>(action, { ...o, method: o.method ?? opts.method }),
    onSuccess: c.onSuccess,
    onError: c.onError,
  });
}

export function useBulk(name: string, opts: CommonOpts<BulkResult, BulkOp> = {}) {
  const api = useApi();
  const c = useCommon<BulkResult, BulkOp>(name, opts, { success: 'Applied', error: 'Bulk action failed' });
  return useMutation<BulkResult, ApiError, BulkOp>({
    mutationFn: ({ op, ids, data }) => api.resource(name).bulk(op, ids, data),
    onSuccess: c.onSuccess,
    onError: c.onError,
  });
}

/** Generic query helper for non-resource endpoints (stats, reports, cross-cutting lists). */
export function useApiQuery<T>(key: readonly unknown[], path: string, opts: { enabled?: boolean; staleTime?: number; query?: Record<string, string | number | boolean | null | undefined> } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: key,
    queryFn: () => api.request<T>(path, { query: opts.query }),
    enabled: opts.enabled ?? true,
    staleTime: opts.staleTime,
  });
}

/**
 * Cursor-paged reads of a non-resource endpoint answering `{ data, next_cursor }` (e.g. a ticket's
 * timeline, newest first): rows of every loaded page in order, plus `loadMore` for the next page.
 */
export function useApiPages<T>(key: readonly unknown[], path: string, opts: { enabled?: boolean; query?: Record<string, string | number | boolean | null | undefined> } = {}) {
  const api = useApi();
  const q = useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam }) => api.request<{ data: T[]; next_cursor: string | null }>(path, { query: { ...opts.query, ...(pageParam ? { cursor: pageParam as string } : {}) } }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? null,
    enabled: opts.enabled ?? true,
  });
  const rows = useMemo(() => q.data?.pages.flatMap((p) => p.data) ?? [], [q.data]);
  return {
    rows,
    isLoading: q.isLoading,
    error: q.error as ApiError | null,
    hasMore: q.hasNextPage ?? false,
    loadingMore: q.isFetchingNextPage,
    loadMore: () => void q.fetchNextPage(),
    refetch: () => void q.refetch(),
  };
}

export type { UseMutationOptions };
export { useMeta } from '../meta/useMeta';
